import { bootstrapApplication } from '@angular/platform-browser';
import { defineCustomElements } from '@ionic/pwa-elements/loader';
import { appConfig } from './app/app.config';
import { App } from './app/app';
import { App as CapApp } from '@capacitor/app';
import { environment } from './environments/environment';
import * as SentryCapacitor from '@sentry/capacitor';
import * as SentryAngular from '@sentry/angular';
import packageInfo from '../package.json';
import { normalizeHttpErrorResponseEvents } from './app/core/utils/sentry-http-error.util';
import { installReadableStreamAsyncIteratorPolyfill } from './app/core/utils/readable-stream-async-iterator.polyfill';

// Must run before any lazy-loaded feature can import `pdfjs-dist` — some
// WKWebView builds lack `ReadableStream.prototype[Symbol.asyncIterator]`,
// which `pdfjs-dist`'s `getTextContent()` relies on via `for await...of`.
// See the polyfill's own doc comment for the full root-cause writeup.
installReadableStreamAsyncIteratorPolyfill();

/**
 * Bootstraps the application.
 *
 * Sentry is initialized before Angular starts so errors thrown during app
 * initialization are captured. When a DSN is present, `App.getInfo()` is called
 * first to attach the native `release` (`version+build`) and `dist` (`build`)
 * to every Sentry event. On web/PWA where `App.getInfo()` is unavailable,
 * `release` falls back to the `version` field in `package.json` and `dist` is omitted.
 *
 * NOTE: `browserTracingIntegration` is intentionally omitted from `integrations`
 * because it injects `baggage` and `sentry-trace` headers on every outgoing HTTP
 * request, which triggers CORS preflight failures on the API server (which does
 * not allow those headers). Angular router tracing is handled by `TraceService`
 * in `app.config.ts` instead.
 *
 * `@sentry/capacitor` and `@sentry/angular` must be kept on matching sibling
 * versions — `@sentry/capacitor`'s own postinstall script (`check-siblings.js`)
 * enforces this and fails `npm install` otherwise. As of `@sentry/capacitor@4.2.0`
 * + `@sentry/angular@10.60.0`, both resolve to the same `@sentry/core`/`@sentry/browser`
 * (10.60.0, deduped by npm), so `SentryAngular.init` no longer needs an `as unknown`
 * cast to satisfy `SentryCapacitor.init`'s second parameter — don't reintroduce one
 * unless a future version bump reintroduces a real type divergence.
 *
 * `beforeSend: normalizeHttpErrorResponseEvents` (2026-08-05) rewrites a raw
 * `HttpErrorResponse` event into one with a real exception `type`/`value`
 * instead of Sentry's generic "Object captured as exception with keys: ..."
 * fallback — see that function's own doc comment (`sentry-http-error.util.ts`)
 * for why this needs to live in `beforeSend` specifically (it's the only hook
 * every event passes through regardless of capture path) rather than only in
 * `SentryService.captureException()`.
 */
(async () => {
  let sentryRelease: string | undefined;
  let sentryDist: string | undefined;

  if (environment.sentryDsn) {
    try {
      const { version, build } = await CapApp.getInfo();
      sentryRelease = `${version}+${build}`;
      sentryDist = build;
    } catch {
      // Web / PWA environment — App.getInfo() is native-only; use package.json version instead.
      sentryRelease = packageInfo.version;
    }

    // Determine environment from configuration.
    let sentryEnvironment = 'development';
    if (environment.production) {
      sentryEnvironment = 'production';
    } else if (environment.mock) {
      sentryEnvironment = 'mock';
    } else if (environment.apiUrl.includes('localhost')) {
      sentryEnvironment = 'local';
    } else if (environment.apiUrl.includes('capi-uat')) {
      sentryEnvironment = 'uat';
    }

    SentryCapacitor.init(
      {
        dsn: environment.sentryDsn,
        integrations: [SentryCapacitor.capacitorRewriteFramesIntegration],
        tracesSampleRate: 1.0,
        tracePropagationTargets: ['localhost'],
        release: sentryRelease,
        dist: sentryDist,
        environment: sentryEnvironment,
        beforeSend: normalizeHttpErrorResponseEvents,
      },
      SentryAngular.init,
    );
  }

  defineCustomElements(window);

  bootstrapApplication(App, appConfig).catch((err) => console.error(err));
})();
